/**
 * f16.1: pure presentation helpers for the dashboard.
 *
 * Absence and zero are different statements (BR-F16.1-1/2): a `null` count
 * renders "Not in your scope", never `0`. Every rule here reads what the
 * server sent -- no role code, no scope set.
 */

import type { DashboardCycle, DashboardScoped } from '@/api/client';
import { formatCycleDate } from '@/routes/cycles/cycleView';

export const NOT_IN_SCOPE = 'Not in your scope';

const INTEGER = new Intl.NumberFormat('en-US');

/** `1,284`; `null` stays `null` so the caller must decide what absence reads as. */
export function formatCount(value: number | null | undefined): string | null {
  return value == null ? null : INTEGER.format(value);
}

/** "1 inactive", or "All active" at zero. */
export function inactiveFoot(inactive: number | null | undefined): string | null {
  if (inactive == null) return null;
  return inactive === 0 ? 'All active' : `${INTEGER.format(inactive)} inactive`;
}

/** "8 reporting USD · 1 reporting EUR". */
export function currencyFoot(units: DashboardScoped['count_feet']['units_by_currency']): string | null {
  if (units == null) return null;
  if (units.length === 0) return 'No active units';
  return units.map((entry) => `${INTEGER.format(entry.count)} reporting ${entry.currency_code}`).join(' · ');
}

/** "1,284 delivery elements in cycle". */
export function elementsFoot(elements: number | null | undefined, hasCycle: boolean): string {
  if (!hasCycle) return 'No cycle yet';
  if (elements == null) return NOT_IN_SCOPE;
  return `${INTEGER.format(elements)} delivery ${elements === 1 ? 'element' : 'elements'} in cycle`;
}

/** Whether every visible level is empty: a global caller on a fresh installation. */
export function nothingImported(counts: DashboardScoped['counts']): boolean {
  const values = [counts.business_unit_groups, counts.business_units, counts.clients, counts.engagements];
  return values.every((value) => value === 0);
}

/** "-6.2%", "+1.0%". */
export function formatPercent(value: string | null | undefined): string | null {
  if (value == null) return null;
  const negative = value.startsWith('-');
  const body = value.replace(/^[+-]/, '');
  const shown = body.includes('.') ? body : `${body}.0`;
  return `${negative ? '-' : /[1-9]/.test(body) ? '+' : ''}${shown}%`;
}

/** "Mid Jul 2026 COM" -> "Mid Jul COM", the prototype's short foot. */
export function shortComparison(label: string | null | undefined): string | null {
  if (!label) return null;
  return label.replace(/\s\d{4}\s/, ' ');
}

/** The next f10.2 milestone on or after `today`, e.g. "freeze on 09 Sep 2026". */
export function nextMilestone(cycle: DashboardCycle, today: Date = new Date()): string | null {
  const iso = today.toISOString().slice(0, 10);
  const next = cycle.derived.calendar.milestones.find((milestone) => milestone.date >= iso);
  if (!next) return null;
  return `${next.key.toLowerCase()} on ${formatCycleDate(next.date)}`;
}

/** Where the page's "Open cycle" button goes: the workspace for the tier that prepares, else the list. */
export function cycleActionPath(body: DashboardScoped): string {
  if (body.cycle && body.capabilities.open_workflow) return `/cycles/${body.cycle.id}`;
  return '/cycles';
}

/** The unit's forecast grid (f11.1). */
export function forecastPath(cycleId: string, unitId: string): string {
  return `/cycles/${cycleId}/forecast?bu=${encodeURIComponent(unitId)}`;
}

export const ATTENTION_LABEL: Readonly<Record<string, string>> = {
  UNMATCHED_ROLE_HOLDERS: 'Role holders matched no user',
  UNMAPPED_RESOURCE_BU: 'Unmapped Resource BU values',
  VALIDATION_WARNINGS: 'Lines with validation warnings',
  HIGH_RISK_CLIENTS: 'Clients with H risk to COM',
};

export const ATTENTION_PILL: Readonly<Record<string, string>> = {
  UNMATCHED_ROLE_HOLDERS: 'pill-warning',
  UNMAPPED_RESOURCE_BU: 'pill-warning',
  VALIDATION_WARNINGS: 'pill-info',
  HIGH_RISK_CLIENTS: 'pill-danger',
};
